import React, { useState } from 'react';
import { Box, Menu, MenuItem, alpha, ListItemIcon } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import DeletionConfirmationDialog from '../dialogs/DeletionConfirmationDialog';
import _ from 'lodash';

const CustomAccordion = ({
  data,
  columns,
  additionalMenuOptions,
  deleteData,
  handleEdit,
  editEnabled = true,
  deleteEnabled = true,
  entityName = '',
  meta = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const dialog = useSelector((state) => state.dialog);
  const dispatch = useDispatch();
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const extraMenuOptions = additionalMenuOptions?.() ?? [];
  const menuOptions = [
    {
      name: 'Edit',
      icon: ModeEditOutlineOutlinedIcon,
      action: () => handleEdit(data),
    },
    {
      name: 'Delete',
      icon: DeleteOutlineIcon,
      action: () => dispatch({ type: 'dialog/set', openDeletionConfirmationDialog: { open: true, props: {} } }),
    },
    ...extraMenuOptions,
  ];

  const accessorkeys = {
    Vehicle: data.entityName,
    Driver: `${data.firstName} ${data.lastName}`,
    Alert: data.entityName,
    User: data.email,
    tags: data.tagValue,
    ApiKeys: data.entityName,
    Maintenance: data.scheduleName,
  };
  const handleMenuItemClick = (e, menuOpt) => {
    e.stopPropagation();
    menuOpt.action();
    handleClose();
  };

  const getDisplayValue = (entry, data) => {
    if (entry?.Cell) {
      const table = { options: { meta } };
      return entry.Cell(entry.accessorFn ? { cell: entry.accessorFn(data), table } : { cell: data[entry.accessorKey], table });
    } else {
      return entry?.accessorFn ? entry.accessorFn(data) : data[entry?.accessorKey];
    }
  };

  // Extract first two key-value pairs
  const firstTwoEntries = columns.slice(0, 2);
  const firstHeaderValue = getDisplayValue(firstTwoEntries[0], data);
  const secondHeaderValue = getDisplayValue(firstTwoEntries[1], data);
  const handleDeletion = (entityId) => {
    deleteData(entityId);
    dispatch({ type: 'dialog/set', openDeletionConfirmationDialog: { open: false, props: {} } });
  };
  return (
    <>
      <Accordion
        expanded={isExpanded}
        sx={{
          background: theme.palette.background.main,
          boxShadow: 'none',
          borderBottom: '1px solid #F6F6F6',
          '&:before': {
            display: 'none',
          },
        }}
      >
        {/* Here videocam icon is displayed only for events to show the event video and in other cases we use this for additional buttons */}
        {!isExpanded && (
          <AccordionSummary
            expandIcon={extraMenuOptions.length > 0 || editEnabled || deleteEnabled ? <MoreHorizIcon onClick={handleClick} /> : null}
            aria-controls='panel-content'
            id='panel-header'
            sx={{
              '&.Mui-expanded': {
                mt: '20px',
                minHeight: '25px',
                height: '25px',
              },
              '&.MuiAccordionSummary-root': {
                minHeight: '56px',
                height: '56px',
              },
              '.MuiAccordionSummary-content': {
                margin: '0',
                alignItems: 'center',
              },
              p: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexGrow: 1 }}>
              <KeyboardArrowRightIcon
                sx={{ alignSelf: 'center', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  // onExpandedChange();
                  setIsExpanded(!isExpanded);
                }}
              />
              <>
                <Typography
                  variant='body2'
                  component='div'
                  sx={{
                    minWidth: '125px',
                    width: '125px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme.palette.gray90,
                  }}
                >
                  {firstHeaderValue}
                </Typography>
                <Typography variant='body2' component='div' sx={{ color: theme.palette.gray90 }}>
                  {secondHeaderValue}
                </Typography>
              </>
            </Box>
          </AccordionSummary>
        )}
        {/* Render Body */}
        <AccordionDetails sx={{ p: 0 }}>
          <div style={{ marginTop: '8px' }}>
            {columns.map((col, colIndex) => {
              const processVal = col.accessorFn ? col.accessorFn(data) : _.get(data, col.accessorKey);
              let displayVal = processVal;
              if (col?.Cell) {
                const table = { options: { meta } };
                displayVal = col.Cell({ cell: processVal, table });
              }
              return (
                <Box
                  key={colIndex}
                  sx={{
                    display: 'flex',
                    height: '40px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {colIndex === 0 && (
                      <ExpandMoreIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsExpanded(!isExpanded);
                        }}
                      />
                    )}
                  </Box>
                  {/* Label */}
                  <Typography variant='body2b' sx={{ minWidth: '150px', fontWeight: 'bold', pl: '8px', color: theme.palette.gray90 }}>
                    {col.header}
                  </Typography>

                  {/* Value */}
                  <Typography
                    variant='body2'
                    component='div'
                    sx={{
                      flexGrow: 1,
                      textAlign: 'left',
                      width: '125px',
                      color: theme.palette.gray90,
                    }}
                  >
                    {displayVal}
                  </Typography>
                  {/* Here videocam icon is displayed only for events to show the event video and in other cases we use this for additional buttons */}
                  {(extraMenuOptions.length > 0 || editEnabled || deleteEnabled) && colIndex === 0 ? <MoreHorizIcon onClick={handleClick} /> : null}
                </Box>
              );
            })}
          </div>
        </AccordionDetails>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{
            borderRadius: '6px',
            '& .MuiPaper-root': {
              background: theme.palette.background.main,
              borderRadius: 2,
              minWidth: 213,
              color: 'rgb(55, 65, 81)',
              py: '6px',
              boxShadow:
                'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
              '& .MuiMenu-list': {
                padding: '0px 0',
              },
              '& .MuiMenuItem-root': {
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                  marginRight: theme.spacing(1),
                },
                padding: '2px 2px 2px 16px',
                minHeight: '36px',
                height: '36px',
                '&:active': {
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                },
                '&:hover': {
                  backgroundColor: theme.palette.background.light5,
                },
              },
            },
          }}
        >
          {menuOptions.map((menuOpt) => {
            if (menuOpt.name === 'Dummy') return null;
            if (menuOpt.name === 'Edit' && !editEnabled) return null;
            if (menuOpt.name === 'Delete' && (!deleteData || !editEnabled)) return null;
            const styles = menuOpt.name === 'Delete' ? { color: 'red' } : {};
            return (
              <MenuItem
                key={menuOpt.name}
                dense
                onClick={(e) => handleMenuItemClick(e, menuOpt)}
                sx={{ fontSize: '14px', '&:hover': { backgroundColor: theme.palette.background.light5 } }}
              >
                {menuOpt.icon ? (
                  <ListItemIcon sx={{ color: (theme) => theme.palette.gray90, ...styles }}>
                    <menuOpt.icon fontSize='20px' />
                    <Box ml='8px'>{menuOpt.name}</Box>
                  </ListItemIcon>
                ) : (
                  <></>
                )}
              </MenuItem>
            );
          })}
        </Menu>
      </Accordion>
      {/* Deletion confirmation dialog  */}
      {dialog.openDeletionConfirmationDialog ? (
        <DeletionConfirmationDialog row={data} handleDelete={handleDeletion} deleteText={accessorkeys[entityName]} />
      ) : null}
    </>
  );
};

export default CustomAccordion;
